import * as React from 'react';
import qs from 'qs';

import AdminSisSyncStatusLabel from 'admin/rostering/components/AdminSisSyncStatusLabel';
import Button from 'components/ButtonDeprecated';
import TitleHeader from 'components/TitleHeader';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Text from 'components/Text';
import {getApiEventConfigurations, getApiSyncType} from '../../../syncSetup/lib/syncSetupData';
import {
  syncSetupConstants,
} from 'syncSetup/lib/syncSetupData';
import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus as DistrictStatus,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus_schoolMappings as SchoolMapping,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  orgUuid: string;
  districtStatus: DistrictStatus;
  childOrganizations: ChildOrganization[];
  canOnlySeeReviewScreen: boolean;
  canOnlySeeRosteringStagingArea: boolean;
};

export default class AdminDistrictSisDashboardApiHeader extends React.Component<Props> {
  numSchoolsToMap = (schoolMappings: SchoolMapping[], childOrgs: ChildOrganization[]) => {
    const numPaidChildOrgs = childOrgs.filter(org => org.paidAt).length;
    const numMappedChildOrgs = schoolMappings.filter(mapping => mapping.remindSchoolUuid).length;
    return numPaidChildOrgs - numMappedChildOrgs;
  };

  renderMatchingBanner = () => {
    const { orgUuid, districtStatus } = this.props;
    const { syncType } = districtStatus;
    const { requiresOauth } = syncSetupConstants[syncType];
    const syncTypeLabel = getApiSyncType(syncType);

    const queryParams = qs.stringify({
      district_uuid: orgUuid,
      sync_type: syncType,
      logged_in: !requiresOauth,
    });
    const button = (
      <Button
        eventElementName={getApiEventConfigurations(syncTypeLabel).matchSchoolsFromSisDashboard}
        href={`/sync_setup/matching?${queryParams}`}>
        Match schools
      </Button>
    );

    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="mascot" actionElement={button}>
        <React.Fragment>
          Finish setup by mapping your schools from{' '}
          {syncTypeLabel} correctly.
        </React.Fragment>
      </AdminNoticeBanner>
    );
  };

  renderAwaitingReviewBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="pencil" actionElement={null}>
        Our implementation team is reviewing your data. You'll receive an email with next steps in
        two business days.
      </AdminNoticeBanner>
    );
  };

  renderStagingEnvironmentBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="percussion" actionElement={null}>
        Review each school's sync to fix errors. When you're ready to push data live, contact our
        implementation team.
      </AdminNoticeBanner>
    );
  };

  renderSubtitle = (isNotActive: boolean) => {
    if (!isNotActive) {
      return <Text>Click on a school to see details.</Text>;
    }
  };

  renderSetupBanner = () => {
    const { orgUuid, districtStatus } = this.props;
    const { syncType } = districtStatus;
    const { requiresOauth } = syncSetupConstants[syncType];

    const syncTypeLabel = getApiSyncType(syncType);
    const eventElementName = getApiEventConfigurations(syncTypeLabel).startAPISetup;
    const queryParams = qs.stringify({
      district_uuid: orgUuid,
      sync_type: syncType,
    });
    const queryPath = requiresOauth ? '/sync_setup' : '/sync_setup/auth_details';

    const button = (
      <Button
        href={`${queryPath}?${queryParams}`}
        eventElementName={eventElementName}>
        Start {syncTypeLabel} setup
      </Button>
    );

    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="mascot" actionElement={button}>
        Next, begin sharing data with Remind.
      </AdminNoticeBanner>
    );
  };

  renderBanner = (noSchoolsMapped: boolean, showMatchingBanner: boolean) => {
    const { canOnlySeeReviewScreen, canOnlySeeRosteringStagingArea } = this.props;

    if (noSchoolsMapped) {
      return this.renderSetupBanner();
    }

    if (showMatchingBanner) {
      return this.renderMatchingBanner();
    }

    if (canOnlySeeReviewScreen) {
      return this.renderAwaitingReviewBanner();
    }

    if (canOnlySeeRosteringStagingArea) {
      return this.renderStagingEnvironmentBanner();
    }

    return null;
  };

  renderTitle = (isNotActive: boolean) => {
    if (isNotActive) {
      return <AdminSisSyncStatusLabel status="pending" />;
    }
    return <AdminSisSyncStatusLabel status="active" />;
  };

  render() {
    const {
      childOrganizations,
      orgUuid,
      districtStatus,
      canOnlySeeReviewScreen,
      canOnlySeeRosteringStagingArea,
    } = this.props;

    const { schoolMappings } = districtStatus;

    const noSchoolsMapped = schoolMappings.length === 0;
    const someSchoolsNotMapped = this.numSchoolsToMap(schoolMappings, childOrganizations) > 0;

    const showMatchingBanner = !noSchoolsMapped && someSchoolsNotMapped;
    const isNotActive = noSchoolsMapped || canOnlySeeReviewScreen || canOnlySeeRosteringStagingArea;

    return (
      <TitleHeader
        title={this.renderTitle(isNotActive)}
        subtext={this.renderSubtitle(isNotActive)}
        actionPlacement="right"
        className="AdminDistrictSisDashboardApiHeader">
        {this.renderBanner(noSchoolsMapped, showMatchingBanner)}
      </TitleHeader>
    );
  }
}
